import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import Logo from '../../assets/logo_jothi.jpg';
import { PageLayout, Table, Button } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { getBranches } from "../../utils/utils";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Same look as the Ledger Report's account statement: a single table, no summary
// cards, and the total in bold under the last row.
const LoanOutstandingReport = () => {
  const { data: authData, roleName } = useAuth();
  const isSuperAdmin = roleName === "superadmin";
  const ownBranchId = Array.isArray(authData?.branchId) ? authData.branchId[0] : authData?.branchId || null;

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(isSuperAdmin ? "" : ownBranchId);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    getBranches().then(setBranches).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branchId) params.branchId = branchId;
      const res = await axios.get("/api/v2/loan-outstanding-report", {
        baseURL: API_BASE_URL,
        params,
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("LoanOutstandingReport load error:", err);
      setRows([]);
    }
    setLoading(false);
  }, [branchId]);

  useEffect(() => { loadData(); }, [loadData]);

  const q = search.toLowerCase();
  const filtered = rows.filter((r) =>
    (r.customer_name      || "").toLowerCase().includes(q) ||
    (r.customer_unique_id || "").toLowerCase().includes(q) ||
    (r.loan_id            || "").toLowerCase().includes(q) ||
    String(r.mobile_number || "").includes(q)
  );

  const totalLoanAmount  = filtered.reduce((s, r) => s + Number(r.loan_amount || 0), 0);
  const totalCollected   = filtered.reduce((s, r) => s + Number(r.collected || 0), 0);
  const totalOutstanding = filtered.reduce((s, r) => s + Number(r.outstanding || 0), 0);

  const branchName = branchId
    ? (branches.find((b) => String(b.value) === String(branchId))?.label || "")
    : "All Branches";

  const exportCSV = () => {
    const headers = ["S.No","Customer ID","Customer Name","Mobile","Loan ID","Loan Amount","Tenure (Months)","Dues Paid","Dues Pending","Collected","Outstanding"];
    const csvRows = [
      headers.join(","),
      ...filtered.map((r, i) => [
        i + 1,
        r.customer_unique_id || "",
        `"${r.customer_name || ""}"`,
        r.mobile_number || "",
        `"${r.loan_id || ""}"`,
        r.loan_amount || 0,
        r.tenure_months ?? "",
        r.dues_paid,
        r.dues_pending,
        r.collected,
        r.outstanding,
      ].join(",")),
      ["", "", "TOTAL", "", "", totalLoanAmount.toFixed(2), "", "", "", totalCollected.toFixed(2), totalOutstanding.toFixed(2)].join(","),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "loan_outstanding_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const toolbar = (
    <div className="flex flex-row flex-wrap items-end gap-3 w-full bg-white py-2.5 px-4 rounded-xl border border-border-light shadow-sm print:hidden">
      {isSuperAdmin && (
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="text-[10px] font-bold text-ink-500 uppercase tracking-wider">Branch</label>
          <select
            value={branchId || ""}
            onChange={(e) => setBranchId(e.target.value)}
            className="h-[32px] bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 text-xs px-2 focus:outline-none"
          >
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>
      )}
      <div className="flex-1 min-w-[240px] flex flex-col gap-1">
        <label className="text-[10px] font-bold text-ink-500 uppercase tracking-wider">Search</label>
        <input
          placeholder="Name / customer ID / loan ID / mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-[32px] bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 text-xs px-3 focus:outline-none transition-all"
        />
      </div>
      <div className="flex flex-row items-end gap-2">
        <Button onClick={exportCSV} variant="secondary" size="sm" className="h-[32px] font-bold text-xs">
          <i className="fa-solid fa-file-csv mr-1.5"></i>
          Export CSV
        </Button>
        <Button onClick={() => window.print()} variant="secondary" size="sm" className="h-[32px] font-bold text-xs">
          <i className="fa-solid fa-print mr-1.5"></i>
          Print
        </Button>
      </div>
    </div>
  );

  return (
    <PageLayout toolbar={toolbar} className="gap-0 pt-0">
      {loading && <Loader message="Loading loan outstanding..." />}

      <div className="flex flex-col gap-2.5 min-h-0 flex-1 print:overflow-visible">
        {/* Print only header */}
        <div className="hidden print:flex flex-col items-center text-center mb-6">
          <img src={Logo} alt="Jothilinga Logo" className="w-28 h-auto object-contain mb-2" />
          <h1 className="text-xl font-black text-ink-950 uppercase tracking-wide">Jothilinga Financial Solutions Pvt. Ltd.</h1>
          <p className="text-xs text-ink-500 font-medium">Loan Outstanding Report</p>
        </div>

        <div className="flex items-center justify-between border-b border-border-light pb-2">
          <div className="flex flex-col">
            <h3 className="text-lg font-black text-ink-900 flex items-center gap-2">
              <i className="fa-solid fa-book-open text-primary"></i>
              {branchName} Loan Outstanding Report
            </h3>
            <p className="text-xs text-ink-500 font-medium">
              As on: <span className="font-semibold text-ink-700">{today()}</span>
            </p>
          </div>
          <span className="bg-primary/5 text-primary border border-primary/20 rounded-xl px-3 py-1 text-xs font-bold">
            {filtered.length} loans
          </span>
        </div>

        <div className="flex flex-col min-h-0 flex-1">
          <Table className="min-h-[380px] max-h-[calc(100vh-300px)] print:max-h-none print:overflow-visible">
            <Table.Head>
              <tr>
                <Table.HeadCell align="center" className="w-14">S.No</Table.HeadCell>
                <Table.HeadCell align="center" className="w-32">Customer ID</Table.HeadCell>
                <Table.HeadCell align="left">Customer Name</Table.HeadCell>
                <Table.HeadCell align="center" className="w-28">Loan ID</Table.HeadCell>
                <Table.HeadCell align="right" className="w-32">Loan Amount (₹)</Table.HeadCell>
                <Table.HeadCell align="center" className="w-24">Tenure (M)</Table.HeadCell>
                <Table.HeadCell align="center" className="w-24">Dues Paid</Table.HeadCell>
                <Table.HeadCell align="center" className="w-24">Dues Pending</Table.HeadCell>
                <Table.HeadCell align="right" className="w-32">Collected (₹)</Table.HeadCell>
                <Table.HeadCell align="right" className="w-36">Outstanding (₹)</Table.HeadCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {filtered.length === 0 ? (
                <Table.EmptyRow colSpan={10} message={loading ? "Loading…" : "No outstanding loans found."} />
              ) : (
                filtered.map((r, idx) => (
                  <Table.Row key={r.member_id}>
                    <Table.Cell align="center" className="text-ink-400">{idx + 1}</Table.Cell>
                    <Table.Cell align="center" className="font-semibold text-primary font-mono text-xs">{r.customer_unique_id || "—"}</Table.Cell>
                    <Table.Cell align="left" className="font-semibold text-ink-800">{r.customer_name || "—"}</Table.Cell>
                    <Table.Cell align="center" className="text-ink-700 text-xs">{r.loan_id || "—"}</Table.Cell>
                    <Table.Cell align="right" className="text-ink-700">{fmt(r.loan_amount)}</Table.Cell>
                    <Table.Cell align="center">{r.tenure_months ?? "—"}</Table.Cell>
                    <Table.Cell align="center" className="font-semibold text-emerald-600">{r.dues_paid}</Table.Cell>
                    <Table.Cell align="center" className="font-semibold text-amber-600">{r.dues_pending}</Table.Cell>
                    <Table.Cell align="right" className="font-semibold text-emerald-600">{fmt(r.collected)}</Table.Cell>
                    <Table.Cell align="right" className="font-bold text-ink-900">{fmt(r.outstanding)}</Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          {/* shrink-0 so the total is never squeezed out by the table above */}
          <div className="flex items-center justify-between border-t-2 border-ink-900 mt-2 pt-3 px-3 shrink-0 bg-white">
            <span className="text-sm font-black text-ink-900 uppercase tracking-wide">
              Total Outstanding
            </span>
            <div className="flex items-baseline gap-6">
              <span className="text-xs font-bold text-ink-500">
                Loan Amount: <span className="text-ink-800">₹{fmt(totalLoanAmount)}</span>
              </span>
              <span className="text-xs font-bold text-ink-500">
                Collected: <span className="text-emerald-600">₹{fmt(totalCollected)}</span>
              </span>
              <span className="text-lg font-black text-ink-900">₹{fmt(totalOutstanding)}</span>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default LoanOutstandingReport;
